import { cx } from 'class-variance-authority';
import { type ComponentPropsWithoutRef, useEffect } from 'react';

import useDisplayAffirm from '@/app/hooks/useDisplayAffirm';
import affirmRefresh from '@/utils/affirmRefresh';

export { default as getAffirmPrice } from './getAffirmPrice';
/** Affirm Docs: https://docs.affirm.com/affirm-developers/docs/html-reference */

interface AffirmPromoProps extends ComponentPropsWithoutRef<'span'> {
  /** price represented as USD cents - i.e. $10 === 1000 */
  readonly price: number;
  /** where the promo is located */
  readonly pageType:
    | 'homepage'
    | 'landing'
    | 'search'
    | 'category'
    | 'product'
    | 'cart'
    | 'payment'
    | 'banner';
  /** classes to change rendering - */
  readonly affirmClass?:
    | 'affirm-as-low-as'
    | 'affirm-site-modal'
    | 'affirm-product-modal';
  /** show learn more link in promo */
  readonly showLearnMore?: boolean;
  /** whether to show dagger footnote marker */
  readonly footnote?: JSX.Element;
}

const AFFIRM_MINIMUM_PRICE = 15000; // $150

const AffirmPromo = ({
  price,
  pageType,
  footnote,
  affirmClass = 'affirm-as-low-as',
  showLearnMore = false,
  className = '',
}: AffirmPromoProps): JSX.Element | null => {
  const shouldDisplay = useDisplayAffirm();

  // Affirm.refresh once mounted.
  useEffect(() => {
    if (shouldDisplay) affirmRefresh();
  }, [price, shouldDisplay]);

  if (!shouldDisplay) return null;

  return (
    <>
      {price >= AFFIRM_MINIMUM_PRICE && (
        <div
          className={cx({
            '[&>a]:whitespace-nowrap [&>a]:text-sandstone-500 [&>a]:underline':
              showLearnMore,
          })}
          data-cy="affirm-promo"
        >
          <span
            className={cx(affirmClass, className)}
            data-page-type={pageType}
            data-amount={price}
            data-learnmore-show={showLearnMore}
          />
          {footnote}
        </div>
      )}
    </>
  );
};

export default AffirmPromo;
